import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import TableComponent from '../../../components/Table/TableComponent';
import SearchIcon from '@mui/icons-material/Search';
import { TableRow, TableCell } from '@mui/material';
import Chart from '../../../components/Chart';

import { BASE_URL, getAxiosConfig } from '../../../components/interface/Config';
import DateRangePicker from '../../../components/FormFilelds/DateRangePicker';
import { ClearButton } from '../../../components/Button/iconbutton';
import ChartButton from '../../../components/Button/ChartButton';
import IconButton from '../../../components/Buttons/SubjectiveIcons';
import TablePaginationComponent from '../../../components/TablePaginationComponent';
import { GFR, ObjectiveResponse } from '../../../components/interface/ObjectiveInterface';

const GFRForm: React.FC<{ patientId: string }> = ({ patientId }) => {
  const [gfrRecords, setGfrRecords] = useState<GFR[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openDialog, setOpenDialog] = useState(false);
  const [editRecord, setEditRecord] = useState<GFR | null>(null);
  const [showChart, setShowChart] = useState(false);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({ startDate: '', endDate: '' });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('success');
  const [showClearFilterButton, setShowClearFilterButton] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [recordToDelete, setRecordToDelete] = useState<GFR | null>(null)
  const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    fetchGfrRecords();
    setShowClearFilterButton(!!(searchTerm || (dateRange.startDate && dateRange.endDate)));

  }, [page, rowsPerPage, searchTerm, dateRange]);

  const fetchGfrRecords = async (startDate: string = '', endDate: string = '') => {
    setLoading(true);
    
    try {
      const response = await axios.get<ObjectiveResponse>(`${BASE_URL}/resource/vitals`, {
        params: {
          limit: rowsPerPage,
          page: page + 1,
          from: startDate,
          to: endDate,
          order_by: 'details->date',
          dir: 2,
          user_id: patientId,
          slug: 'gfr',
          searchkey: searchTerm || undefined,
        },
        headers: getAxiosConfig().headers,
      });

      const vitalsData = response.data.data.vitals;
      const total = response.data.data.pagination!.total;
      if (Array.isArray(vitalsData) && vitalsData.length > 0) {
        const formattedData: GFR[] = vitalsData.map((vital) => ({
          id: vital.id,
          user_id: vital.user_id, // Ensure to include this if available
          consult_id: vital.consult_id,
          peripheral_id: vital.peripheral_id,
          slug: 'gfr', // Ensure to include this if available
          details: {
            date: format(new Date(vital.details.date), 'dd-MM-yyyy'),
            time: vital.details.time,
            gfr: vital.details.gfr,
            range_code: vital.details.range_code,
            gfrFlag: vital.details.gfrFlag,
            gfrFlagColor: vital.details.gfrFlagColor,
            unit: vital.details.unit
          },
          freeze: vital.freeze,
          document: vital.document,
        }));

        setGfrRecords(formattedData);
        setTotalRecords(total ?? 0);
      } else {
        console.warn('No vitals data received.'); // Warn if no data
        setGfrRecords([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error('Error fetching blood ketone records:', error);
      showSnackbar('Failed to fetch blood ketone records. Please try again later.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDateRange({ startDate: '', endDate: '' });
    setPage(0);
    setRowsPerPage(5);
    fetchGfrRecords();
    setShowClearFilterButton(false);
  };

  const renderRow = (item: GFR, index: number) => (
    <TableRow key={index}>
      <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'}}> {page * rowsPerPage + index + 1}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'}}>{(item.details.date)}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'}}>{item.details.time}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'}}>{item.details.gfr}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'}}>
        <span
          style={{
            backgroundColor: item.details.range_code,
            padding: '4px 8px',
            borderRadius: '8px',
            color: '#FFFFFF',
          }}
        >{item.details.gfrFlag}</span>
      </TableCell>
    </TableRow>
  );

  const chartData = {
    labels: gfrRecords.map(record => record.details.date),
    datasets: [
      {
        label: 'GFR (mL/min/1.73 m²)',
        data: gfrRecords.map(record => Number(record.details.gfr)), // Ensure gfr is a number
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderWidth: 1,
      },
    ],
  };


  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${context.raw} mL/min/1.73 m²`,
        },
      },
    },
  };

  return (
    <div>
      <div className="flex items-end justify-between w-full gap-12">
        <div className="flex flex-grow space-x-2 gap-2">
          <DateRangePicker
            startDateId="GFR_Start_Date"
            endDateId="GFR_End_Date"
            onChange={(dates: { startDate: string; endDate: string }) => setDateRange(dates)}
            value={dateRange}
          />
          
            <IconButton icon={<SearchIcon />} onClick={() => fetchGfrRecords(dateRange.startDate, dateRange.endDate)} />
            {showClearFilterButton && <ClearButton onClick={handleClearFilters} />}
        </div>
        <div className="flex space-x-4">
    
        {gfrRecords.length > 0 && (
         <ChartButton text={showChart ? 'List' : 'Chart'} onClick={() => setShowChart(prev => !prev)} />
         )}
         </div>
      </div>

      {loading ? (

<div className="text-center my-4">
 
</div>
) : (
<>
  {showChart ? (
    <Chart data={chartData} options={chartOptions} />
  ) : (
    <div>
          <TableComponent
            headers={['S.No', 'Date', 'Time', 'Value (mL/min/1.73 m²)', 'Result']}
            data={gfrRecords}
            renderRow={renderRow}
            loading={loading} error={''}
          />
        
        </div>
          )}
        </>
      )}
      <TablePaginationComponent
        count={totalRecords}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_event, newPage) => {
          setPage(newPage);
        }}
        onRowsPerPageChange={(event) => {
          const newRowsPerPage = parseInt(event.target.value, 10);
          setRowsPerPage(newRowsPerPage);
          setPage(0);
        }}
      />
    </div>
  );
};

export default GFRForm;
